import { StrictMode, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster, toast } from 'sonner'
import { AuthProvider } from '@/hooks/use-auth'
import { I18nProvider, useT } from '@/i18n'
import { useNewVersion } from '@/hooks/use-new-version'
import { ThemeProvider, initializeTheme, useTheme } from '@/components/theme-provider'
import { initSentry, Sentry } from '@/lib/sentry'
import { routeTree } from './routeTree.gen'
import './index.css'

// Before anything else, so an exception thrown during startup is still reported. A reporter
// initialised inside a component never sees the crash that stopped that component mounting.
initSentry()
initializeTheme()

// Supabase appends the recovery token to the redirect URL as a hash fragment, and that redirect
// URL must be on the project's allow-list. `/reset-password` is not, so recovery links land on the
// app root — where the index route bounces to /login before anything can read the fragment, and the
// person is left logged out with no way to set a password.
//
// Redirect here, BEFORE the router mounts: a route-level redirect runs before any component effect,
// so doing this in a component is always too late.
if (window.location.hash.includes('type=recovery') && window.location.pathname !== '/reset-password') {
  window.location.replace(`/reset-password${window.location.hash}`)
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
})

const router = createRouter({ routeTree, defaultPreload: 'intent' })

function AppToaster() {
  const { resolvedTheme } = useTheme()
  return <Toaster position="top-right" richColors theme={resolvedTheme} />
}

/**
 * Tells the person a deploy has happened. Most people never reload, so without this they keep
 * using the build they loaded days ago and report bugs that are already fixed.
 *
 * The toast does not expire and cannot be swiped away, because a dismissed notice would defeat
 * the point. It reloads only when they choose to, so a half-typed quote is never thrown away.
 */
function NewVersionPrompt() {
  const stale = useNewVersion()
  const { t } = useT()
  const shown = useRef(false)

  useEffect(() => {
    if (!stale || shown.current) return
    shown.current = true
    toast.info(t('app.newVersion'), {
      description: t('app.newVersionBody'),
      duration: Infinity,
      dismissible: false,
      action: { label: t('app.reload'), onClick: () => window.location.reload() },
    })
  }, [stale, t])

  return null
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/**
 * The last line of defence. Without a boundary, one render error unmounts the whole tree and the
 * trade person gets a white screen with no way back — the worst possible failure, because it looks like
 * the product simply stopped existing.
 */
function Crashed({ resetError }: { resetError: () => void }) {
  return (
    <div className="grid min-h-screen place-items-center bg-canvas p-6">
      <div className="max-w-md rounded-[14px] border border-token bg-token-surface p-6 text-center">
        <h1 className="font-display text-[18px] font-semibold text-token-primary">Something went wrong</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-token-secondary">
          The problem has been reported. Nothing you had saved is lost.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <button
            type="button" onClick={resetError}
            className="inline-flex h-9 items-center rounded-[10px] bg-brand px-4 text-[14px] font-medium text-brand-fg"
          >
            Try again
          </button>
          <button
            type="button" onClick={() => window.location.assign('/dashboard')}
            className="inline-flex h-9 items-center rounded-[10px] border border-token px-4 text-[14px] font-medium text-token-primary"
          >
            Back to the dashboard
          </button>
        </div>
      </div>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ resetError }) => <Crashed resetError={resetError} />}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            {/* Inside the auth provider: the language follows the signed-in account (spec 001). */}
            <I18nProvider>
              <RouterProvider router={router} />
              <NewVersionPrompt />
            </I18nProvider>
          </AuthProvider>
          <AppToaster />
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
